'use client';

import * as React from 'react';
import type * as Runtime from './dialog-runtime';
import { createContextStore } from '@eliadmin/core/src/stores/context-store';
import { usePathname } from 'next/navigation';

// This boundary contains only React: a closed/empty overlay never imports its UI engine.
class OverlayLoadBoundary extends React.Component<
  React.PropsWithChildren<{ onRetry: () => void }>,
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div role="alert" className="fixed bottom-4 right-4 z-[1001] rounded-lg border bg-background p-4 shadow-lg">
        <p>Obsah sa nepodarilo načítať.</p>
        <button type="button" onClick={this.props.onRetry} className="mr-4 underline">
          Skúsiť znova
        </button>
        <button type="button" onClick={() => window.location.reload()} className="underline">
          Obnoviť stránku
        </button>
      </div>
    );
  }
}

/** Keep ref/prop contracts; a retry creates a fresh React.lazy instead of reusing a rejected one. */
export function deferOverlayComponent<T extends React.ElementType>(
  load: () => Promise<T>,
  displayName: string,
  getResolved?: () => T | undefined,
): T {
  const Deferred = React.forwardRef<unknown, Record<string, unknown>>((props, ref) => {
    const [attempt, setAttempt] = React.useState(0);
    const Lazy = React.useMemo(
      () => React.lazy(async () => ({
        default: await load() as unknown as React.ComponentType<Record<string, unknown>>,
      })),
      // The loader is captured once by this factory; only a user retry replaces it.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [attempt],
    );
    const Resolved = getResolved?.() as React.ComponentType<Record<string, unknown>> | undefined;
    return (
      <OverlayLoadBoundary key={attempt} onRetry={() => setAttempt(value => value + 1)}>
        {Resolved ? (
          <Resolved {...props} ref={ref} />
        ) : (
          <React.Suspense fallback={<span className="sr-only" role="status">Načítava sa obsah</span>}>
            <Lazy {...props} ref={ref} />
          </React.Suspense>
        )}
      </OverlayLoadBoundary>
    );
  });
  Deferred.displayName = displayName;
  return Deferred as unknown as T;
}

// Resolve the whole module before mounting Root so Content, Title and focusable siblings
// use the same loaded implementation synchronously (no nested lazy mount gaps).
let loadedRuntime: typeof Runtime | undefined;
function loadRuntime() {
  return import('./dialog-runtime').then(runtime => {
    loadedRuntime = runtime;
    return runtime;
  });
}

const Dialog = deferOverlayComponent<typeof Runtime.Dialog>(
  () => loadRuntime().then(runtime => runtime.Dialog),
  'Dialog',
  () => loadedRuntime?.Dialog,
);

const DialogTrigger = deferOverlayComponent<typeof Runtime.DialogTrigger>(
  () => loadRuntime().then(runtime => runtime.DialogTrigger),
  'DialogTrigger',
  () => loadedRuntime?.DialogTrigger,
);

const DialogPortal = deferOverlayComponent<typeof Runtime.DialogPortal>(
  () => loadRuntime().then(runtime => runtime.DialogPortal),
  'DialogPortal',
  () => loadedRuntime?.DialogPortal,
);

const DialogClose = deferOverlayComponent<typeof Runtime.DialogClose>(
  () => loadRuntime().then(runtime => runtime.DialogClose),
  'DialogClose',
  () => loadedRuntime?.DialogClose,
);

const DialogOverlay = deferOverlayComponent<typeof Runtime.DialogOverlay>(
  () => loadRuntime().then(runtime => runtime.DialogOverlay),
  'DialogOverlay',
  () => loadedRuntime?.DialogOverlay,
);

const DialogContent = deferOverlayComponent<typeof Runtime.DialogContent>(
  () => loadRuntime().then(runtime => runtime.DialogContent),
  'DialogContent',
  () => loadedRuntime?.DialogContent,
);

const DialogHeader = deferOverlayComponent<typeof Runtime.DialogHeader>(
  () => loadRuntime().then(runtime => runtime.DialogHeader),
  'DialogHeader',
  () => loadedRuntime?.DialogHeader,
);

const DialogFooter = deferOverlayComponent<typeof Runtime.DialogFooter>(
  () => loadRuntime().then(runtime => runtime.DialogFooter),
  'DialogFooter',
  () => loadedRuntime?.DialogFooter,
);

const DialogTitle = deferOverlayComponent<typeof Runtime.DialogTitle>(
  () => loadRuntime().then(runtime => runtime.DialogTitle),
  'DialogTitle',
  () => loadedRuntime?.DialogTitle,
);

const DialogDescription = deferOverlayComponent<typeof Runtime.DialogDescription>(
  () => loadRuntime().then(runtime => runtime.DialogDescription),
  'DialogDescription',
  () => loadedRuntime?.DialogDescription,
);

type DialogConfig = {
  id: string;
  content: React.ReactNode;
  onClose?: () => void;
};

type DialogStoreState = {
  dialogs: Record<string, DialogConfig>;
};

type DialogStoreActions = {
  open: (config: DialogConfig) => void;
  close: (id: string) => void;
  closeAll: () => void;
};

const [DialogContextProvider, useDialogStore] = createContextStore<
  DialogStoreState,
  DialogStoreActions
>(
  {
    dialogs: {},
  },
  set => ({
    open: (config: DialogConfig) =>
      set(state => ({
        dialogs: {
          ...state.dialogs,
          [config.id]: config,
        },
      })),
    close: (id: string) =>
      set(state => {
        const dialogs = { ...state.dialogs };
        delete dialogs[id];
        return { dialogs };
      }),
    closeAll: () =>
      set(() => ({
        dialogs: {},
      })),
  }),
);

/**
 * It is important to always put dialog provider under context provider you want to use in dialog content.
 */
function DialogProvider(props: React.PropsWithChildren) {
  return (
    <DialogContextProvider>
      <DialogProviderRenderer />
      {props.children}
    </DialogContextProvider>
  );
}

function DialogProviderRenderer() {
  const dialogs = useDialogStore(s => s.dialogs);
  const close = useDialogStore(s => s.close);
  const closeAll = useDialogStore(s => s.closeAll);

  const pathname = usePathname();

  React.useEffect(() => {
    closeAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <>
      {Object.values(dialogs).map(dialog => (
        <Dialog
          key={dialog.id}
          open
          onOpenChange={open => {
            if (!open) {
              dialog.onClose?.();
              close(dialog.id);
            }
          }}
        >
          {dialog.content}
        </Dialog>
      ))}
    </>
  );
}

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogProvider,
  DialogTitle,
  DialogClose,
  DialogTrigger,
  useDialogStore,
};
export type { DialogConfig };
